/**
 * Simple Event Bus for decoupled communication between components
 * Supports typed events and provides cleanup mechanisms
 */

import type {
  BaseEvent,
  EventListener,
  EventSubscription,
  ILanguageChangedEvent,
  ThemeChangedEvent,
} from '@app-types/events';

// Event bus class
export class APUtilEventBus {
  private listeners = new Map<string, Set<EventListener>>();
  private onceListeners = new Map<string, Set<EventListener>>();
  private debugMode = false;

  /**
   * Enable debug mode to log all events
   */
  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Subscribe to events of a specific type
   */
  public on<T extends BaseEvent>(eventType: string, listener: EventListener<T>): EventSubscription {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    this.listeners.get(eventType)!.add(listener as EventListener);

    return {
      unsubscribe: () => this.off(eventType, listener),
    };
  }

  /**
   * Subscribe to events of a specific type (one-time only)
   */
  public once<T extends BaseEvent>(
    eventType: string,
    listener: EventListener<T>
  ): EventSubscription {
    if (!this.onceListeners.has(eventType)) {
      this.onceListeners.set(eventType, new Set());
    }

    this.onceListeners.get(eventType)!.add(listener as EventListener);

    return {
      unsubscribe: () => this.removeOnceListener(eventType, listener),
    };
  }

  /**
   * Unsubscribe from events
   */
  public off<T extends BaseEvent>(eventType: string, listener: EventListener<T>): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.delete(listener as EventListener);
      if (listeners.size === 0) {
        this.listeners.delete(eventType);
      }
    }

    this.removeOnceListener(eventType, listener);
  }

  /**
   * Emit an event to all subscribers
   */
  public async emit<T extends BaseEvent>(
    eventType: string,
    eventData: Omit<T, 'type' | 'timestamp'>
  ): Promise<void> {
    const event: T = {
      type: eventType,
      timestamp: Date.now(),
      ...eventData,
    } as T;

    if (this.debugMode) {
      console.log(`[EventBus] Emitting event: ${eventType}`, event);
    }

    // Execute regular listeners
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      const promises: Promise<void>[] = [];
      listeners.forEach((listener) => {
        try {
          const result = listener(event);
          if (result instanceof Promise) {
            promises.push(result);
          }
        } catch (error) {
          console.error(`[EventBus] Error in listener for ${eventType}:`, error);
        }
      });

      // Wait for all async listeners
      if (promises.length > 0) {
        await Promise.allSettled(promises);
      }
    }

    // Execute once listeners and remove them
    const onceListeners = this.onceListeners.get(eventType);
    if (onceListeners) {
      const promises: Promise<void>[] = [];
      onceListeners.forEach((listener) => {
        try {
          const result = listener(event);
          if (result instanceof Promise) {
            promises.push(result);
          }
        } catch (error) {
          console.error(`[EventBus] Error in once-listener for ${eventType}:`, error);
        }
      });

      // Clear once listeners after execution
      this.onceListeners.delete(eventType);

      // Wait for all async listeners
      if (promises.length > 0) {
        await Promise.allSettled(promises);
      }
    }
  }

  /**
   * Remove all listeners for a specific event type
   */
  public removeAllListeners(eventType?: string): void {
    if (eventType) {
      this.listeners.delete(eventType);
      this.onceListeners.delete(eventType);
    } else {
      this.listeners.clear();
      this.onceListeners.clear();
    }
  }

  /**
   * Get all registered event types
   */
  public getEventTypes(): string[] {
    const types = new Set([...this.listeners.keys(), ...this.onceListeners.keys()]);
    return Array.from(types);
  }

  /**
   * Get listener count for an event type
   */
  public getListenerCount(eventType: string): number {
    const regularCount = this.listeners.get(eventType)?.size || 0;
    const onceCount = this.onceListeners.get(eventType)?.size || 0;
    return regularCount + onceCount;
  }

  /**
   * Check if there are any listeners for an event type
   */
  public hasListeners(eventType: string): boolean {
    return this.getListenerCount(eventType) > 0;
  }

  /**
   * Remove a once listener
   */
  private removeOnceListener<T extends BaseEvent>(
    eventType: string,
    listener: EventListener<T>
  ): void {
    const onceListeners = this.onceListeners.get(eventType);
    if (onceListeners) {
      onceListeners.delete(listener as EventListener);
      if (onceListeners.size === 0) {
        this.onceListeners.delete(eventType);
      }
    }
  }
}

// Define common dashboard events

export interface UserPreferencesUpdatedEvent extends BaseEvent {
  type: 'user:preferences:updated';
  preferences: Record<string, unknown>;
}

export interface DataRefreshEvent extends BaseEvent {
  type: 'data:refresh';
  source?: string;
}

// Use types from types/events.ts
// LanguageChangedEvent -> ILanguageChangedEvent (imported)
// ThemeChangedEvent (imported)

// Create and export singleton instance
export const eventBus = new APUtilEventBus();

// Development helpers
if (import.meta.env.DEV) {
  eventBus.setDebugMode(true);

  // Add global access for debugging
  (globalThis as { __eventBus?: APUtilEventBus }).__eventBus = eventBus;
}

// Utility functions for common event patterns
export const createEventSubscription = <T extends BaseEvent>(
  eventType: string,
  listener: EventListener<T>
): EventSubscription => {
  return eventBus.on(eventType, listener);
};

export interface UseEventBusReturn {
  emit: typeof eventBus.emit;
  on: typeof eventBus.on;
  off: typeof eventBus.off;
  once: typeof eventBus.once;
}

// Helper for creating typed event emitters
export const createTypedEventEmitter = <T extends BaseEvent>(eventType: string) => ({
  emit: (eventData: Omit<T, 'type' | 'timestamp'>) => eventBus.emit<T>(eventType, eventData),
  on: (listener: EventListener<T>) => eventBus.on<T>(eventType, listener),
  off: (listener: EventListener<T>) => eventBus.off<T>(eventType, listener),
  once: (listener: EventListener<T>) => eventBus.once<T>(eventType, listener),
});

// Language Change Event Emitter
export const languageEventEmitter =
  createTypedEventEmitter<ILanguageChangedEvent>('language:changed');

// Theme Change Event Emitter
export const themeEventEmitter = createTypedEventEmitter<ThemeChangedEvent>('theme:changed');
